import React, { useState, useEffect } from 'react';
import { createJob, updateJob, uploadFile, getCountryEnums } from '../../services/adminApi';
import './JobForm.css';

// Simple Rich Text Editor Component with Preview
const RichTextEditor = ({ label, value, onChange, rows = 5 }) => {
  const [isPreview, setIsPreview] = useState(false);
  const [htmlValue, setHtmlValue] = useState(value || '');

  useEffect(() => {
    setHtmlValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setHtmlValue(newValue);
    onChange(newValue);
  };

  const cleanHtml = (html) => {
    if (!html) return '';
    return String(html)
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  };

  return (
    <div className="rich-text-editor">
      <div className="editor-header">
        <label>{label}</label>
        <button
          type="button"
          onClick={() => setIsPreview(!isPreview)}
          className="preview-toggle"
        >
          {isPreview ? 'Редактировать' : 'Предпросмотр'}
        </button>
      </div>
      {isPreview ? (
        <div className="preview-content" dangerouslySetInnerHTML={{ __html: cleanHtml(htmlValue) }} />
      ) : (
        <textarea
          value={htmlValue}
          onChange={handleChange}
          rows={rows}
          placeholder="Введите текст. Можно использовать HTML теги: &lt;br&gt;, &lt;span&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt; и т.д."
        />
      )}
      {!isPreview && (
        <div className="editor-hint">
          <small>Подсказка: Используйте HTML теги для форматирования. Например: &lt;br&gt; для новой строки, &lt;strong&gt; для жирного текста</small>
        </div>
      )}
    </div>
  );
};

const JobForm = ({ job, onClose }) => {
  const [formData, setFormData] = useState({
    NAME: '',
    PREVIEW_TEXT: '',
    DETAIL_TEXT: '',
    PREVIEW_PICTURE: null,
    DETAIL_PICTURE: null,
    CODE: '',
    PRICE: '',
    COUNTRY: '',
    ACTIVE: 'Y',
    SORT: 500
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [countryEnums, setCountryEnums] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);

  useEffect(() => {
    loadCountryEnums();
  }, []);

  useEffect(() => {
    if (job) {
      setFormData({
        NAME: job.NAME || '',
        PREVIEW_TEXT: job.PREVIEW_TEXT || '',
        DETAIL_TEXT: job.DETAIL_TEXT || '',
        PREVIEW_PICTURE: job.PREVIEW_PICTURE || null,
        DETAIL_PICTURE: job.DETAIL_PICTURE || null,
        CODE: job.CODE || '',
        PRICE: job.PROPERTIES?.PRICE?.VALUE || '',
        COUNTRY: job.PROPERTIES?.COUNTRY?.VALUE || job.PROPERTIES?.COUNTRY?.['~VALUE']?.TEXT || '',
        ACTIVE: job.ACTIVE || 'Y',
        SORT: job.SORT || 500
      });
    }
  }, [job]);

  const loadCountryEnums = async () => {
    try {
      setLoadingCountries(true);
      const enums = await getCountryEnums();
      setCountryEnums(enums);
    } catch (error) {
      console.error('Error loading country enums:', error);
      alert('Не удалось загрузить список стран');
    } finally {
      setLoadingCountries(false);
    }
  };

  const handleFileChange = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadFile(file);
      setFormData({ ...formData, [type]: result.fileId });
    } catch (error) {
      console.error('Upload error:', error);
      alert('Не удалось загрузить файл');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.NAME || !formData.NAME.trim()) {
      alert('Пожалуйста, укажите название вакансии');
      return;
    }
    
    setSaving(true);

    try {
      const jobData = {
        NAME: formData.NAME.trim(),
        PREVIEW_TEXT: formData.PREVIEW_TEXT || '',
        DETAIL_TEXT: formData.DETAIL_TEXT || '',
        PREVIEW_PICTURE: formData.PREVIEW_PICTURE || null,
        DETAIL_PICTURE: formData.DETAIL_PICTURE || null,
        CODE: formData.CODE ? formData.CODE.trim() : null,
        PROPERTIES: {}
      };

      // Add PRICE to properties if provided
      if (formData.PRICE && formData.PRICE.trim()) {
        jobData.PROPERTIES.PRICE = formData.PRICE;
      }

      // Add COUNTRY to properties if selected (must be a valid enum ID)
      if (formData.COUNTRY && formData.COUNTRY.trim()) {
        jobData.PROPERTIES.COUNTRY = formData.COUNTRY;
      }

      jobData.ACTIVE = formData.ACTIVE || 'Y';
      jobData.SORT = parseInt(formData.SORT) || 500;

      if (job) {
        await updateJob(job.ID, jobData);
      } else {
        await createJob(jobData);
      }
      
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Неизвестная ошибка';
      const errorDetails = error.response?.data?.details;
      console.error('Error details:', error.response?.data);
      alert(`Не удалось сохранить вакансию: ${errorMessage}${errorDetails ? '\n\nДетали: ' + errorDetails.substring(0, 200) : ''}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{job ? 'Редактировать вакансию' : 'Создать вакансию'}</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <form onSubmit={handleSubmit} className="job-form">
          <div className="form-group">
            <label>Название *</label>
            <input
              type="text"
              value={formData.NAME}
              onChange={(e) => setFormData({ ...formData, NAME: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Код (URL)</label>
            <input
              type="text"
              value={formData.CODE}
              onChange={(e) => setFormData({ ...formData, CODE: e.target.value })}
              placeholder="job-code-name"
            />
          </div>

          <div className="form-group">
            <label>Страна</label>
            <select
              value={formData.COUNTRY}
              onChange={(e) => setFormData({ ...formData, COUNTRY: e.target.value })}
              disabled={loadingCountries}
            >
              <option value="">-- Выберите страну --</option>
              {countryEnums.map((country) => (
                <option key={country.ID} value={country.ID}>
                  {country.VALUE}
                </option>
              ))}
            </select>
            {loadingCountries && <small>Загрузка стран...</small>}
          </div>

          <RichTextEditor
            label="Краткое описание"
            value={formData.PREVIEW_TEXT}
            onChange={(value) => setFormData({ ...formData, PREVIEW_TEXT: value })}
            rows={3}
          />

          <RichTextEditor
            label="Полное описание"
            value={formData.DETAIL_TEXT}
            onChange={(value) => setFormData({ ...formData, DETAIL_TEXT: value })}
            rows={8}
          />

          <div className="form-row">
            <div className="form-group">
              <label>Изображение для превью</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'PREVIEW_PICTURE')}
                disabled={uploading}
              />
            </div>

            <div className="form-group">
              <label>Детальное изображение</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'DETAIL_PICTURE')}
                disabled={uploading}
              />
            </div>
          </div>

          {uploading && <div className="upload-status">Загрузка...</div>}

          <div className="form-group">
            <label>Цена</label>
            <input
              type="text"
              value={formData.PRICE}
              onChange={(e) => setFormData({ ...formData, PRICE: e.target.value })}
              placeholder="от $3 500"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Активна</label>
              <select
                value={formData.ACTIVE}
                onChange={(e) => setFormData({ ...formData, ACTIVE: e.target.value })}
              >
                <option value="Y">Да</option>
                <option value="N">Нет</option>
              </select>
            </div>

            <div className="form-group">
              <label>Порядок сортировки</label>
              <input
                type="number"
                value={formData.SORT}
                onChange={(e) => setFormData({ ...formData, SORT: e.target.value })}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-cancel">Отмена</button>
            <button type="submit" disabled={saving} className="btn-save">
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobForm;
